import { useState } from 'react';
import {
  BrandProgram,
  BrandRequirement,
  RequirementStatus,
  RequirementCadence,
  usePrograms,
  useCreateProgram,
  usePatchProgram,
  useDeleteProgram,
  useCreateRequirement,
  usePatchRequirement,
  useDeleteRequirement,
} from '../../lib/api/programs';
import { Icon } from '../ui/Icon';
import { useToast } from '../ui/Toast';

// Explicit palette so the pills read the same regardless of which status
// tokens exist. bg is the same hue at ~12% alpha.
const STATUS_META: Record<RequirementStatus, { label: string; color: string }> = {
  not_started: { label: 'Not started', color: '#8A867C' },
  on_track: { label: 'On track', color: '#3B5CFF' },
  at_risk: { label: 'At risk', color: '#E8A33E' },
  met: { label: 'Met', color: '#2B8A5A' },
  missed: { label: 'Missed', color: '#E2542C' },
};
const STATUS_ORDER: RequirementStatus[] = ['not_started', 'on_track', 'at_risk', 'met', 'missed'];
const CADENCE_LABEL: Record<RequirementCadence, string> = {
  '': '',
  weekly: 'Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  semiannual: 'Semiannual',
  ongoing: 'Ongoing',
};

const metaLabelStyle: React.CSSProperties = {
  fontSize: 9.5,
  textTransform: 'uppercase',
  letterSpacing: '0.07em',
  color: 'var(--ink-4)',
  fontWeight: 600,
};

function StatusSelect({
  value,
  onChange,
}: {
  value: RequirementStatus;
  onChange: (s: RequirementStatus) => void;
}) {
  const meta = STATUS_META[value];
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as RequirementStatus)}
      aria-label="Requirement status"
      style={{
        appearance: 'none',
        border: `1px solid ${meta.color}`,
        color: meta.color,
        background: `${meta.color}1E`,
        fontWeight: 600,
        fontSize: 11.5,
        borderRadius: 999,
        padding: '3px 22px 3px 10px',
        cursor: 'pointer',
        // little chevron
        backgroundImage:
          `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='${encodeURIComponent(meta.color)}' stroke-width='2.5' stroke-linecap='round'><path d='M6 9l6 6 6-6'/></svg>")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 6px center',
      }}
    >
      {STATUS_ORDER.map((s) => (
        <option key={s} value={s} style={{ color: 'var(--ink)' }}>
          {STATUS_META[s].label}
        </option>
      ))}
    </select>
  );
}

function RequirementRow({
  brandId,
  programId,
  req,
}: {
  brandId: string;
  programId: number;
  req: BrandRequirement;
}) {
  const toast = useToast();
  const patch = usePatchRequirement(brandId, programId);
  const del = useDeleteRequirement(brandId, programId);
  const [confirmDel, setConfirmDel] = useState(false);
  const [note, setNote] = useState(req.notes);

  const meta = [req.pillar, CADENCE_LABEL[req.cadence], req.weight && `Weight ${req.weight}`]
    .filter(Boolean)
    .join(' · ');

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: 12,
        padding: '12px 0',
        borderTop: '1px solid var(--border-soft)',
        alignItems: 'start',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {req.is_gate && (
            <span
              title="Gate — necessary but not sufficient; missing it caps the whole score"
              style={{
                fontSize: 9.5,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                fontWeight: 700,
                color: '#B4232A',
                background: '#FCE6DE',
                padding: '2px 6px',
                borderRadius: 4,
              }}
            >
              Gate
            </span>
          )}
          <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--ink)' }}>{req.title}</span>
        </div>
        {req.target && (
          <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 3 }}>
            <span style={{ ...metaLabelStyle, marginRight: 6 }}>Target</span>
            {req.target}
          </div>
        )}
        {req.detail && (
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4, lineHeight: 1.45 }}>
            {req.detail}
          </div>
        )}
        {meta && <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 5 }}>{meta}</div>}
        <input
          value={note}
          placeholder="Progress note…"
          onChange={(e) => setNote(e.target.value)}
          onBlur={() => {
            if (note !== req.notes) {
              patch.mutate(
                { id: req.id, patch: { notes: note } },
                { onError: () => toast.push('Could not save the note') },
              );
            }
          }}
          style={{
            marginTop: 8,
            width: '100%',
            maxWidth: 460,
            fontSize: 12,
            padding: '5px 8px',
            border: '1px solid var(--border)',
            borderRadius: 6,
            background: 'var(--bg)',
            color: 'var(--ink-2)',
          }}
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <StatusSelect
          value={req.status}
          onChange={(status) =>
            patch.mutate(
              { id: req.id, patch: { status } },
              { onError: () => toast.push('Could not update status') },
            )
          }
        />
        {confirmDel ? (
          <button
            onClick={() =>
              del.mutate(req.id, {
                onSuccess: () => toast.push('Requirement removed'),
                onError: () => toast.push('Could not remove it'),
              })
            }
            style={{
              fontSize: 11,
              color: '#B4232A',
              background: '#FCE6DE',
              border: 'none',
              borderRadius: 6,
              padding: '4px 8px',
              cursor: 'pointer',
            }}
          >
            Confirm
          </button>
        ) : (
          <button
            aria-label="Remove requirement"
            title="Remove"
            onClick={() => {
              setConfirmDel(true);
              window.setTimeout(() => setConfirmDel(false), 3000);
            }}
            style={{
              display: 'inline-flex',
              border: 'none',
              background: 'transparent',
              color: 'var(--ink-4)',
              cursor: 'pointer',
              padding: 4,
            }}
          >
            <Icon name="x" size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

function ProgramCard({ brandId, program }: { brandId: string; program: BrandProgram }) {
  const toast = useToast();
  const patchProgram = usePatchProgram(brandId);
  const createReq = useCreateRequirement(brandId, program.id);
  const delProgram = useDeleteProgram(brandId);
  const [editing, setEditing] = useState(false);
  const [newReq, setNewReq] = useState('');
  const [confirmDel, setConfirmDel] = useState(false);

  const met = program.requirements.filter((r) => r.status === 'met').length;
  const total = program.requirements.length;

  const terms: [string, string][] = [
    ['Front discount', program.front_discount],
    ['Backend rebate', program.rebate_ceiling],
    ['Eligibility', program.eligibility_threshold],
    ['Settlement', program.settlement],
  ].filter(([, v]) => !!v) as [string, string][];

  const field = (key: keyof BrandProgram, label: string, wide = false) => (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: wide ? '1 1 100%' : '1 1 180px' }}>
      <span style={metaLabelStyle}>{label}</span>
      <input
        defaultValue={(program[key] as string) ?? ''}
        onBlur={(e) => {
          if (e.target.value !== (program[key] ?? '')) {
            patchProgram.mutate({ id: program.id, patch: { [key]: e.target.value } });
          }
        }}
        style={{
          fontSize: 12.5,
          padding: '5px 8px',
          border: '1px solid var(--border)',
          borderRadius: 6,
          background: 'var(--bg-elevated)',
          color: 'var(--ink)',
        }}
      />
    </label>
  );

  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--r-lg)',
        padding: 16,
        background: 'var(--bg-elevated)',
        marginBottom: 14,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 15, color: 'var(--ink)' }}>{program.name}</div>
          {program.reference_period && (
            <div style={{ fontSize: 12, color: 'var(--ink-4)', marginTop: 2 }}>{program.reference_period}</div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          {total > 0 && (
            <span style={{ fontSize: 12, color: 'var(--ink-3)', fontWeight: 600 }}>
              {met}/{total} met
            </span>
          )}
          <button
            onClick={() => setEditing((v) => !v)}
            style={{
              fontSize: 11.5,
              border: '1px solid var(--border)',
              background: 'var(--bg)',
              borderRadius: 6,
              padding: '4px 9px',
              cursor: 'pointer',
              color: 'var(--ink-3)',
            }}
          >
            {editing ? 'Done' : 'Edit terms'}
          </button>
        </div>
      </div>

      {editing ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 12 }}>
          {field('name', 'Programme name', true)}
          {field('reference_period', 'Reference period')}
          {field('effective_date', 'Effective date (YYYY-MM-DD)')}
          {field('front_discount', 'Front discount')}
          {field('rebate_ceiling', 'Backend rebate')}
          {field('eligibility_threshold', 'Eligibility')}
          {field('settlement', 'Settlement')}
          <label style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: '1 1 100%' }}>
            <span style={metaLabelStyle}>Notes (exclusions, tools, open points)</span>
            <textarea
              defaultValue={program.notes}
              rows={4}
              onBlur={(e) => {
                if (e.target.value !== program.notes) {
                  patchProgram.mutate({ id: program.id, patch: { notes: e.target.value } });
                }
              }}
              style={{
                fontSize: 12.5,
                padding: '6px 8px',
                border: '1px solid var(--border)',
                borderRadius: 6,
                background: 'var(--bg-elevated)',
                color: 'var(--ink)',
                resize: 'vertical',
              }}
            />
          </label>
        </div>
      ) : (
        <>
          {terms.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
              {terms.map(([label, value]) => (
                <span
                  key={label}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'baseline',
                    gap: 6,
                    fontSize: 12,
                    background: 'var(--bg-subtle)',
                    border: '1px solid var(--border-soft)',
                    borderRadius: 6,
                    padding: '4px 9px',
                  }}
                >
                  <span style={metaLabelStyle}>{label}</span>
                  <span style={{ fontWeight: 600, color: 'var(--ink)' }}>{value}</span>
                </span>
              ))}
            </div>
          )}
          {program.notes && (
            <div
              style={{
                fontSize: 12,
                color: 'var(--ink-3)',
                marginTop: 10,
                whiteSpace: 'pre-wrap',
                lineHeight: 1.5,
              }}
            >
              {program.notes}
            </div>
          )}
        </>
      )}

      <div style={{ marginTop: 6 }}>
        {program.requirements.map((req) => (
          <RequirementRow key={req.id} brandId={brandId} programId={program.id} req={req} />
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
        <input
          value={newReq}
          placeholder="Add a requirement…"
          onChange={(e) => setNewReq(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && newReq.trim()) {
              createReq.mutate(
                { title: newReq.trim(), sort_order: program.requirements.length },
                { onSuccess: () => setNewReq(''), onError: () => toast.push('Could not add it') },
              );
            }
          }}
          style={{
            flex: '1 1 auto',
            maxWidth: 320,
            fontSize: 12.5,
            padding: '6px 9px',
            border: '1px solid var(--border)',
            borderRadius: 6,
            background: 'var(--bg)',
          }}
        />
        <button
          disabled={!newReq.trim()}
          onClick={() =>
            createReq.mutate(
              { title: newReq.trim(), sort_order: program.requirements.length },
              { onSuccess: () => setNewReq(''), onError: () => toast.push('Could not add it') },
            )
          }
          style={{
            fontSize: 12,
            border: '1px solid var(--border)',
            background: 'var(--bg)',
            borderRadius: 6,
            padding: '6px 11px',
            cursor: newReq.trim() ? 'pointer' : 'default',
            opacity: newReq.trim() ? 1 : 0.5,
          }}
        >
          Add
        </button>
        <div style={{ flex: 1 }} />
        {confirmDel ? (
          <button
            onClick={() =>
              delProgram.mutate(program.id, {
                onSuccess: () => toast.push('Programme deleted'),
                onError: () => toast.push('Could not delete it'),
              })
            }
            style={{
              fontSize: 11.5,
              color: '#B4232A',
              background: '#FCE6DE',
              border: 'none',
              borderRadius: 6,
              padding: '6px 10px',
              cursor: 'pointer',
            }}
          >
            Delete programme?
          </button>
        ) : (
          <button
            onClick={() => {
              setConfirmDel(true);
              window.setTimeout(() => setConfirmDel(false), 3000);
            }}
            style={{
              fontSize: 11.5,
              color: 'var(--ink-4)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            Delete
          </button>
        )}
      </div>
    </div>
  );
}

export default function BrandComplianceSection({ brandId }: { brandId: string }) {
  const { data: programs = [], isLoading, isError } = usePrograms(brandId);
  const createProgram = useCreateProgram(brandId);
  const toast = useToast();

  // Nothing to show and no error → render a quiet add affordance only.
  const addButton = (
    <button
      onClick={() =>
        createProgram.mutate(
          { name: 'New compliance programme' },
          { onError: () => toast.push('Could not add the programme') },
        )
      }
      style={{
        fontSize: 12,
        border: '1px solid var(--border)',
        background: 'var(--bg)',
        borderRadius: 6,
        padding: '5px 11px',
        cursor: 'pointer',
        color: 'var(--ink-2)',
      }}
    >
      + Add programme
    </button>
  );

  // Most brands have no supplier programme — collapse the whole section to
  // a single quiet button so it doesn't take space on ~200 brand pages.
  // Errors are also kept quiet here: nothing to see when nothing is tracked.
  if (!isLoading && (isError || programs.length === 0)) {
    return (
      <div style={{ padding: '4px 0 8px' }}>
        <button
          type="button"
          onClick={() =>
            createProgram.mutate(
              { name: 'New compliance programme' },
              { onError: () => toast.push('Could not add the programme') },
            )
          }
          style={{
            fontSize: 12,
            border: '1px dashed var(--border-strong)',
            background: 'transparent',
            borderRadius: 6,
            padding: '5px 11px',
            cursor: 'pointer',
            color: 'var(--ink-4)',
          }}
          title="Track a supplier rebate programme (like the FLOS Digital Excellence Program) for this brand"
        >
          + Add rebate programme
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="section">
        <div className="section-head">
          <div className="section-title">Rebate &amp; compliance</div>
          {programs.length > 0 && addButton}
        </div>
      </div>
      {isLoading ? (
        <div style={{ fontSize: 12.5, color: 'var(--ink-4)' }}>Loading…</div>
      ) : (
        programs.map((program) => (
          <ProgramCard key={program.id} brandId={brandId} program={program} />
        ))
      )}
    </>
  );
}
